import React, { useState } from "react";
import { toLocalISODate } from "../lib/dates";
import { toast } from "sonner";
import { ShieldAlert, ChevronDown, Trash2, Fingerprint } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";

export function SecurityNotices() {
  const { data, reload } = useApi("/notifications");
  const items = (data?.notifications || []).filter((n) => n.category === "security");
  const [open, setOpen] = useState(true);
  const del = async (id) => { await api.delete(`/notifications/${id}`); reload(); };
  const clear = async () => { await api.delete("/notifications?category=security"); toast.success("Security notices cleared"); reload(); };
  if (!items.length) return null;
  return (
    <div className="glass rounded-2xl p-5" data-testid="security-notices">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2" aria-expanded={open} data-testid="security-notices-toggle">
          <ChevronDown size={16} className={`text-gold transition-transform ${open ? "" : "-rotate-90"}`} />
          <ShieldAlert size={18} className="text-crimson" /><span className="font-serif-display text-lg font-bold text-crimson">Security notices</span>
          <span className="font-mono-accent text-xs text-muted-foreground">({items.length})</span>
        </button>
        <Button size="sm" variant="ghost" onClick={clear} className="text-xs text-destructive" data-testid="security-notices-clear">Clear all</Button>
      </div>
      {open && <ul className="mt-3 space-y-2">{items.map((n) => (
        <li key={n.id} className={`flex items-start justify-between gap-2 rounded-lg border p-3 text-sm ${n.read ? "border-gold/15 bg-parchment-card" : "border-gold/40 bg-gold-light"}`} data-testid={`security-notice-${n.id}`}>
          <div><p className="font-semibold">{n.title}</p><p className="text-xs text-muted-foreground">{n.body}</p><p className="mt-1 text-[10px] text-muted-foreground">{(n.created_at || "").replace("T", " ").slice(0, 16)}</p></div>
          <button onClick={() => del(n.id)} className="text-destructive" title="Delete" data-testid={`security-notice-delete-${n.id}`}><Trash2 size={14} /></button>
        </li>))}</ul>}
    </div>
  );
}

export function PasskeyPanel() {
  const { data, reload } = useApi("/passkeys");
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;
  const add = async () => {
    setBusy(true);
    try {
      const { data: opts } = await api.post("/passkeys/register/options");
      const credential = await startRegistration({ optionsJSON: opts });
      await api.post("/passkeys/register/complete", { credential, label: navigator.userAgent.includes("iPhone") ? "iPhone" : navigator.userAgent.includes("Android") ? "Android phone" : "This device" });
      toast.success("Face ID / Touch ID enabled on this device"); reload();
    } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Cancelled" : formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/passkeys/${id}`); reload(); };
  return (
    <div className="space-y-2" data-testid="passkey-panel">
      <p className="text-sm font-medium flex items-center gap-2"><Fingerprint size={16} className="text-crimson" /> Face ID / Touch ID sign-in</p>
      <p className="text-xs text-muted-foreground">Add a passkey on this device so you can sign in with Face ID, Touch ID or your phone's screen lock instead of a password.</p>
      {(data || []).map((p) => (
        <div key={p.id} className="flex items-center justify-between rounded-lg border border-gold/20 bg-parchment-card p-2 text-xs" data-testid={`passkey-${p.id}`}>
          <span>{p.label} · added {toLocalISODate(p.created_at)}{p.last_used ? ` · last used ${toLocalISODate(p.last_used)}` : ""}</span>
          <button onClick={() => remove(p.id)} className="text-destructive" data-testid={`passkey-remove-${p.id}`}><Trash2 size={13} /></button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} disabled={busy || !supported} data-testid="passkey-add"><Fingerprint size={14} className="mr-1" /> {supported ? "Enable Face ID / passkey on this device" : "Not supported in this browser"}</Button>
    </div>
  );
}
